import type { ComponentType, SVGProps } from 'react'

import { AgentLogo } from './agent-logo'
import { AskLogo } from './ask-logo'
import { CommunicationsLogo } from './communications-logo'
import { CustomersLogo } from './customers-logo'
import { CustomersStorefrontLogo } from './customers-storefront-logo'
import { FactoryLogo } from './factory-logo'
import { HqLogo } from './hq-logo'
import { MenusLogo } from './menus-logo'
import { PreviewsLogo } from './previews-logo'
import { RfpGremlinLogo } from './rfp-gremlin-logo'
import { RoadWarriorLogo } from './road-warrior-logo'
import { TaskerLogo } from './tasker-logo'

/**
 * One canonical brand record per Ordo app: name, host, sign-in copy, and
 * the static mark `<SigninCard>` falls back to.
 *
 * `bg` / `fg` are exact hex values matching Tailwind's `<color>-100` /
 * `<color>-600` palette — the SOFT variant in `SIDEBAR_TILE_COLORS`.
 * Slate is the one exception: the soft variant uses `-200`/`-700` for
 * better contrast.
 *
 * The LIVE tile is the multipass registry row (HQ → Apps): `<SigninCard>`
 * reads it by `registrySlug`, and the static fields here are the fallback
 * for when that lookup fails. The build-time favicon assets come from the
 * registry too, via the snapshot in `scripts/registry-tiles.ts`.
 */
export interface AppBrand {
  /** Display name above "Sign in to continue". */
  name: string
  /**
   * The app's multipass registry slug (`App.slug`, the `currentAppId` its
   * sidebar passes). `<SigninCard>` and the sign-in favicon read the live
   * tile (icon + colors set in HQ → Apps) for this slug and fall back to the
   * static brand below. `null` = not registry-driven: no live registry row,
   * or a customer-facing app whose mark is intentionally fixed.
   */
  registrySlug: string | null
  /**
   * Tailwind color key matching the entry in `SIDEBAR_TILE_COLORS`.
   * Used by `<SigninCard>` to pick the same tile background as the
   * sidebar's soft variant.
   */
  colorKey: string
  /** Tile fill, hex (Tailwind `<color>-100`), for the fallback signin tile. */
  bg: string
  /** Mark color drawn on top of bg, hex (Tailwind `<color>-600`). */
  fg: string
  /** The `@ordo/ui/logos` mark — single-color, currentColor-driven. */
  Logo: ComponentType<SVGProps<SVGSVGElement>>
  /** Sentence below "Sign in with Google" on the signin card. */
  allowedDomainsLine: string
  /** Production host URL (no trailing slash). Drives the Factory Compare iframe target. */
  host: string
  /** Path of the signin page on this app — most are `/signin`, Next apps use `/login`. */
  signinPath: '/signin' | '/login'
}

const ALLOWED_DOMAINS_LINE = 'Only @ordo.com and @ordoschools.com accounts'

export const APP_BRANDS = {
  factory: {
    name: 'Factory',
    registrySlug: 'factory',
    colorKey: 'indigo',
    bg: '#E0E7FF', // indigo-100
    fg: '#4F46E5', // indigo-600
    Logo: FactoryLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://factory.ordo.com',
    signinPath: '/signin',
  },
  tasker: {
    name: 'Tasks',
    registrySlug: 'tasks',
    colorKey: 'amber',
    bg: '#FEF3C7', // amber-100
    fg: '#D97706', // amber-600
    Logo: TaskerLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://tasks.ordo.com',
    signinPath: '/signin',
  },
  'total-recall': {
    name: 'Ask',
    registrySlug: null,
    colorKey: 'teal',
    bg: '#CCFBF1', // teal-100
    fg: '#0D9488', // teal-600
    Logo: AskLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://ask.ordo.com',
    signinPath: '/signin',
  },
  agent: {
    name: 'Agent',
    registrySlug: 'agent',
    colorKey: 'indigo',
    bg: '#E0E7FF', // indigo-100
    fg: '#4F46E5', // indigo-600
    Logo: AgentLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://agent.ordo.com',
    signinPath: '/signin',
  },
  hq: {
    name: 'HQ',
    registrySlug: 'hq',
    colorKey: 'slate',
    bg: '#E2E8F0', // slate-200
    fg: '#334155', // slate-700
    Logo: HqLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://hq.ordo.com',
    signinPath: '/signin',
  },
  boombox: {
    name: 'Communications',
    registrySlug: 'comms',
    colorKey: 'purple',
    bg: '#F3E8FF', // purple-100
    fg: '#9333EA', // purple-600
    Logo: CommunicationsLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://comms.ordo.com',
    signinPath: '/login',
  },
  'rfp-gremlin': {
    name: 'RFPs',
    registrySlug: 'rfps',
    colorKey: 'blue',
    bg: '#DBEAFE', // blue-100
    fg: '#2563EB', // blue-600
    Logo: RfpGremlinLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://rfps.ordo.com',
    signinPath: '/login',
  },
  'road-warrior': {
    name: 'Schedule',
    registrySlug: 'schedule',
    colorKey: 'orange',
    bg: '#FFEDD5', // orange-100
    fg: '#EA580C', // orange-600
    Logo: RoadWarriorLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://schedule.ordo.com',
    signinPath: '/login',
  },
  previews: {
    name: 'Previews',
    registrySlug: 'previews',
    colorKey: 'lime',
    bg: '#ECFCCB', // lime-100
    fg: '#65A30D', // lime-600
    Logo: PreviewsLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://previews.ordo.com',
    signinPath: '/signin',
  },
  menus: {
    name: 'Menus',
    registrySlug: 'menus',
    colorKey: 'green',
    bg: '#DCFCE7', // green-100
    fg: '#166534', // green-800
    Logo: MenusLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://menubuilder.ordo.com',
    signinPath: '/signin',
  },
  customers: {
    name: 'Customers',
    registrySlug: 'customers',
    colorKey: 'pink',
    bg: '#FCE4EC',
    fg: '#C62828',
    Logo: CustomersStorefrontLogo,
    allowedDomainsLine: ALLOWED_DOMAINS_LINE,
    host: 'https://customers.ordo.com',
    signinPath: '/signin',
  },
  // Customer-facing unified app — apps/ordo (Plan 00 of the apps/ordo
  // plan series). The only brand whose sign-in surface uses
  // `SigninCardFull` (multi-method) instead of `SigninCard` (Google-
  // only); customers may not have Google accounts, so the card also
  // shows magic-link + email/password options.
  ordo: {
    name: 'Ordo',
    registrySlug: null,
    colorKey: 'emerald',
    bg: '#D1FAE5', // emerald-100
    fg: '#059669', // emerald-600
    Logo: CustomersLogo,
    allowedDomainsLine: 'Sign in with your invited email — Google not required',
    host: 'https://app.ordoschools.com',
    signinPath: '/signin',
  },
} as const satisfies Record<string, AppBrand>

export type AppBrandKey = keyof typeof APP_BRANDS

export function getAppBrand(key: AppBrandKey): AppBrand {
  return APP_BRANDS[key]
}
